import { Fragment, Suspense, lazy, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DataFactory } from "n3";
import type { ModelContext } from "../../rdf/model";
import { buildSummary } from "../../rdf/model";
import type {
  DatasetDescriptor,
  TermDetail as TermDetailModel,
  TermSummary,
} from "../../rdf/types";
import { resolveVersion } from "../../rdf/types";
import { VOCAB } from "../../rdf/vocab";
import KindBadge from "../../components/KindBadge";
import PropertyTable from "./PropertyTable";
import ShaclPanel from "./ShaclPanel";
import { explorePath, visualizePath } from "../../state/navigation";
import { useAppState } from "../../state/AppStateContext";

const { namedNode } = DataFactory;

// Cytoscape only loads when a term page actually renders its neighborhood
// preview — keeps the Explorer route's initial chunk free of graph code.
const MiniGraph = lazy(() => import("./MiniGraph"));

const INSTANCES_PREVIEW_LIMIT = 24;

/**
 * Walks up the first superclass/superproperty chain to build a docs-style
 * breadcrumb (Root › … › parent). Multiple inheritance takes the first
 * parent at each level — showing *a* path is the goal, matching the tree.
 */
function buildBreadcrumb(ctx: ModelContext, detail: TermDetailModel): TermSummary[] {
  const predicate =
    detail.superProperties.length > 0 && detail.superClasses.length === 0
      ? VOCAB.rdfs.subPropertyOf
      : VOCAB.rdfs.subClassOf;
  const path: TermSummary[] = [];
  const visited = new Set<string>([detail.iri]);
  let current = detail.iri;
  for (let depth = 0; depth < 12; depth++) {
    const parent = ctx.store
      .getObjects(namedNode(current), namedNode(predicate), null)
      .find((o) => o.termType === "NamedNode" && !visited.has(o.value));
    if (!parent) break;
    visited.add(parent.value);
    path.unshift(buildSummary(ctx, parent.value));
    current = parent.value;
  }
  return path;
}

function RelationSection({ title, terms }: { title: string; terms: TermSummary[] }) {
  if (terms.length === 0) return null;
  return (
    <div className="rounded-xl border border-[var(--oe-border)] bg-[var(--oe-surface)] p-4">
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--oe-text-muted)]">
        {title}
        <span className="ml-1.5 font-normal normal-case tracking-normal">({terms.length})</span>
      </h3>
      <div className="flex flex-wrap gap-1.5">
        {terms.slice(0, INSTANCES_PREVIEW_LIMIT).map((term) => (
          <Link
            key={term.iri}
            to={explorePath(term)}
            title={term.iri}
            className="max-w-full truncate rounded-full border border-[var(--oe-border)] px-2.5 py-1 text-[13px] text-[var(--oe-text)] transition-colors hover:border-[var(--oe-accent)] hover:text-[var(--oe-accent)]"
          >
            {term.label ?? term.prefixedName}
          </Link>
        ))}
        {terms.length > INSTANCES_PREVIEW_LIMIT && (
          <span className="px-1 py-1 text-xs text-[var(--oe-text-muted)]">
            +{terms.length - INSTANCES_PREVIEW_LIMIT} more
          </span>
        )}
      </div>
    </div>
  );
}

function CopyIriButton({ iri }: { iri: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(iri).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="inline-flex items-center gap-1 rounded-md border border-[var(--oe-border)] px-1.5 py-0.5 text-[11px] font-medium text-[var(--oe-text-muted)] transition-colors hover:border-[var(--oe-border-strong)] hover:text-[var(--oe-text)]"
      title="Copy full IRI"
    >
      {copied ? (
        <>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m5 13 4 4L19 7" />
          </svg>
          Copied
        </>
      ) : (
        <>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
            <rect x="9" y="9" width="11" height="11" rx="2" />
            <path d="M5 15V5a2 2 0 0 1 2-2h10" />
          </svg>
          Copy IRI
        </>
      )}
    </button>
  );
}

interface TermDetailProps {
  ctx: ModelContext;
  detail: TermDetailModel;
  datasets: DatasetDescriptor[];
}

export default function TermDetail({ ctx, detail, datasets }: TermDetailProps) {
  const { versionSelections } = useAppState();
  const dataset = datasets.find((d) => d.id === detail.datasetId);
  const version = dataset ? resolveVersion(dataset, versionSelections) : null;
  const breadcrumb = useMemo(() => buildBreadcrumb(ctx, detail), [ctx, detail]);

  return (
    <div className="mx-auto max-w-5xl">
      {breadcrumb.length > 0 && (
        <nav className="mb-3 flex flex-wrap items-center gap-1 text-[13px] text-[var(--oe-text-muted)]" aria-label="Hierarchy">
          {breadcrumb.map((ancestor) => (
            <Fragment key={ancestor.iri}>
              <Link
                to={explorePath(ancestor)}
                className="rounded px-0.5 hover:text-[var(--oe-accent)]"
              >
                {ancestor.label ?? ancestor.prefixedName}
              </Link>
              <span aria-hidden="true" className="text-[var(--oe-border-strong)]">›</span>
            </Fragment>
          ))}
          <span className="px-0.5 font-medium text-[var(--oe-text)]">
            {detail.label ?? detail.prefixedName}
          </span>
        </nav>
      )}

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <KindBadge kind={detail.kind} />
        {dataset && (
          <Link
            to={`/explore/ontologies?dataset=${encodeURIComponent(dataset.id)}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-[var(--oe-border)] px-2 py-0.5 text-[11px] text-[var(--oe-text-muted)] transition-colors hover:border-[var(--oe-border-strong)] hover:text-[var(--oe-text)]"
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: dataset.accentColor }}
            />
            {dataset.shortLabel}
            {version ? ` · ${version.label}` : ""}
          </Link>
        )}
      </div>

      <h1 className="text-2xl font-semibold tracking-tight text-[var(--oe-text)]">
        {detail.label ?? detail.prefixedName}
      </h1>
      <div className="mb-4 mt-1 flex flex-wrap items-center gap-2">
        <code className="font-mono text-[13px] text-[var(--oe-text-muted)]">{detail.prefixedName}</code>
        <CopyIriButton iri={detail.iri} />
      </div>

      {detail.comment && (
        <p className="mb-5 max-w-3xl text-[15px] leading-relaxed text-[var(--oe-text)]">
          {detail.comment}
        </p>
      )}

      <div className="mb-8 flex flex-wrap gap-2">
        <Link
          to={visualizePath(detail.iri)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--oe-accent)] px-3.5 py-2 text-sm font-medium text-[var(--oe-accent-contrast)] transition-opacity hover:opacity-90"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="12" cy="5" r="2" />
            <circle cx="5" cy="17" r="2" />
            <circle cx="19" cy="17" r="2" />
            <path d="M12 7 6 15m6-8 6 8M7 17h10" />
          </svg>
          Open in Graph
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 space-y-4">
          <RelationSection title="Superclasses" terms={detail.superClasses} />
          <RelationSection title="Subclasses" terms={detail.subClasses} />
          <RelationSection title="Super-properties" terms={detail.superProperties} />
          <RelationSection title="Sub-properties" terms={detail.subProperties} />
          <RelationSection title="Domain" terms={detail.domain} />
          <RelationSection title="Range" terms={detail.range} />
          <RelationSection title="Equivalent classes" terms={detail.equivalentClasses} />
          <RelationSection title="Instances" terms={detail.instances} />

          <section className="rounded-xl border border-[var(--oe-border)] bg-[var(--oe-surface)] p-4">
            <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--oe-text-muted)]">
              Properties
            </h2>
            <PropertyTable rows={detail.properties} />
          </section>

          {(detail.shapes.length > 0 || detail.targetedByShapes.length > 0) && (
            <section className="rounded-xl border border-[var(--oe-border)] bg-[var(--oe-surface)] p-4">
              <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--oe-text-muted)]">
                SHACL validation
              </h2>
              <ShaclPanel shapes={detail.shapes} targetedByShapes={detail.targetedByShapes} />
            </section>
          )}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <div className="overflow-hidden rounded-xl border border-[var(--oe-border)] bg-[var(--oe-surface)]">
            <div className="flex items-center justify-between border-b border-[var(--oe-border)] px-4 py-2.5">
              <h2 className="text-[11px] font-semibold uppercase tracking-wider text-[var(--oe-text-muted)]">
                Neighborhood
              </h2>
              <Link
                to={visualizePath(detail.iri)}
                className="text-xs font-medium text-[var(--oe-accent)] hover:underline"
              >
                Full graph →
              </Link>
            </div>
            <div className="h-56">
              <Suspense
                fallback={
                  <div className="flex h-full items-center justify-center text-xs text-[var(--oe-text-muted)]">
                    Loading preview…
                  </div>
                }
              >
                <MiniGraph focusIri={detail.iri} />
              </Suspense>
            </div>
          </div>

          <div className="rounded-xl border border-[var(--oe-border)] bg-[var(--oe-surface)] p-4">
            <h2 className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--oe-text-muted)]">
              Details
            </h2>
            <dl className="space-y-2.5 text-[13px]">
              <div>
                <dt className="text-[var(--oe-text-muted)]">IRI</dt>
                <dd className="break-all font-mono text-xs leading-5">{detail.iri}</dd>
              </div>
              {dataset && (
                <div>
                  <dt className="text-[var(--oe-text-muted)]">Ontology</dt>
                  <dd>
                    {dataset.label}
                    {version ? ` — ${version.label}` : ""}
                  </dd>
                </div>
              )}
              {detail.typeIris.length > 0 && (
                <div>
                  <dt className="text-[var(--oe-text-muted)]">rdf:type</dt>
                  <dd className="font-mono text-xs leading-5">
                    {detail.typeIris.map((iri) => ctx.prefixes.compact(iri)).join(", ")}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </aside>
      </div>
      <div className="h-10" />
    </div>
  );
}
